// Shared shell primitives for the TC prototype: one Composer, one detail peek, asset helpers.
// Composes the vendored @ubm/ui-components sources (Textarea, Button, Drawer, BottomSheet); nothing here redraws them.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent } from "react";
import { ArrowUp } from "lucide-react";
import { Textarea } from "../vendor/ubm-ui-components/src/components/textarea";
import { Button } from "../vendor/ubm-ui-components/src/components/button";
import { Drawer, DrawerContent } from "../vendor/ubm-ui-components/src/components/drawer";
import { BottomSheet } from "../vendor/ubm-ui-components/src/components/bottom-sheet";
import type { PeekDetail } from "./contracts";

/** Existing prototype images and brand files, served next to this prototype. No new image is invented. */
export const assetsPrefix = "../prototype-tc-reprise/assets/";
export const asset = (name: string) => `${assetsPrefix}${name}`;

/** Breakpoint shared by the shell and the peek: below it, dock + bottom sheet; above it, rail + side peek. */
export const MOBILE_QUERY = "(max-width: 767px)";

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback((notify: () => void) => {
    const list = window.matchMedia(query);
    list.addEventListener("change", notify);
    return () => list.removeEventListener("change", notify);
  }, [query]);
  return useSyncExternalStore(subscribe, () => window.matchMedia(query).matches, () => false);
}

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
}

const COMPOSER_MAX_LINES = 8;

/**
 * One line at rest, grows with its content up to eight lines, then scrolls. The radius never changes with the height and
 * no separator sits above it: the hosting surface decides its own spacing. Enter sends, Shift + Enter breaks the line.
 */
export function Composer({ value, onChange, onSend, placeholder = "Écrire un message", disabled = false }: ComposerProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  const canSend = !disabled && value.trim().length > 0;

  useLayoutEffect(() => {
    const node = field.current;
    if (!node) return;
    node.style.height = "auto";
    const style = getComputedStyle(node);
    const line = parseFloat(style.lineHeight) || 20;
    const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const max = line * COMPOSER_MAX_LINES + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + borders;
    const natural = node.scrollHeight + borders;
    node.style.height = `${Math.max(44, Math.min(natural, max))}px`;
    node.style.overflowY = natural > max ? "auto" : "hidden";
  }, [value]);

  const send = () => { if (canSend) onSend(); };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    send();
  };

  return <div className="tc-composer" data-disabled={disabled || undefined}>
    <Textarea ref={field} className="tc-composer-field" rows={1} value={value} placeholder={placeholder} disabled={disabled}
      aria-label={placeholder} onChange={event => onChange(event.target.value)} onKeyDown={onKeyDown} />
    <Button className="tc-composer-send" variant="primary" shape="icon" size="md" aria-label="Envoyer" disabled={!canSend} onClick={send}>
      <ArrowUp size={18} strokeWidth={2} aria-hidden="true" />
    </Button>
  </div>;
}

export interface SharedPeekProps {
  detail: PeekDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mobile: boolean;
}

/**
 * The single detail surface, controlled by App through PageProps.openPeek.
 * Desktop: the Drawer canon NON MODAL (`modal={false}`), contained in a right-hand host so the page stays usable; the veil
 * is not shown. A click outside, Escape or the close control close it through the native onOpenChange(false); focus
 * returns to its origin (shared overlay engine).
 * Mobile: the BottomSheet canon, one shared sheet, no visible cross: the grabber, Escape and a tap on the veil close it.
 */
const PEEK_FOOTER_STYLE: CSSProperties = {
  position: "sticky", bottom: 0, zIndex: 1, display: "flex", justifyContent: "flex-end", gap: "var(--arc-space-2)",
  marginTop: "auto", padding: "var(--arc-space-4) 0 calc(var(--arc-space-4) + env(safe-area-inset-bottom))",
  borderTop: "1px solid var(--arc-border)", background: "var(--arc-surface-raised)",
};

export function SharedPeek({ detail, open, onOpenChange, mobile }: SharedPeekProps) {
  // The last detail stays rendered while the surface leaves, so the exit never flashes empty.
  const [last, setLast] = useState(detail);
  useEffect(() => { if (detail) setLast(detail); }, [detail]);
  const shown = detail ?? last;
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  // Neither DrawerContent nor BottomSheet exposes a footer slot (vendored: header + scrolling body). The shared footer is
  // therefore the last child of that body, sticky at its bottom edge: it stays visible while the content scrolls, once.
  const body = shown && <>
    <div className="tc-peek-body">{shown.content}</div>
    {shown.footer ? <div className="tc-peek-footer" style={{ ...PEEK_FOOTER_STYLE, ...(mobile ? { flexDirection: "column" as const, alignItems: "stretch" } : {}) }}>{shown.footer}</div> : null}
  </>;

  return <>
    <div ref={setHost} className="tc-peek-host" data-open={(open && !mobile) || undefined} />
    {shown && mobile && <BottomSheet open={open} onOpenChange={onOpenChange} title={shown.title} showClose={false} detents={[.6, .92]} initialDetent={shown.footer ? 1 : 0} className="tc-peek-sheet">
      {body}
    </BottomSheet>}
    {shown && !mobile && host && <Drawer open={open} onOpenChange={onOpenChange} modal={false}>
      <DrawerContent side="right" title={shown.title} container={host} className="tc-side-peek" closeLabel="Fermer le détail">
        {body}
      </DrawerContent>
    </Drawer>}
  </>;
}

/** Reward Miles: a single starred token, distinct from consultation credits. */
export function MilesIcon({size=18}: {size?:number}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="m12 6 1.8 3.65 4.03.59-2.92 2.85.69 4.01-3.6-1.89-3.6 1.89.69-4.01-2.92-2.85 4.03-.59Z"/></svg>;
}

export interface PageChrome { title?:string; back?:()=>void; compose?:()=>void; }
let pageChrome:PageChrome={};const chromeListeners=new Set<()=>void>();
export const pageChromeStore={getSnapshot:()=>pageChrome,subscribe:(listener:()=>void)=>{chromeListeners.add(listener);return()=>chromeListeners.delete(listener);}};
export function setPageChrome(next:PageChrome){pageChrome=next;chromeListeners.forEach(listener=>listener());}
